/**
 * ProjectionUpdateBanner - Non-blocking banner for projection updates
 *
 * Shows when projections have been updated while user has customizations.
 * Allows user to keep adjustments or reset to new baseline.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button } from 'omaha-design-system';
import { X } from '@phosphor-icons/react';
import { IRootState } from '../../../../store/reducer';
import {
  resetGameCustomizations,
  setProjectionsUpdatedSinceCustomization,
} from '../../store/slice';

interface ProjectionUpdateBannerProps {
  gamePlayerIds: string[];
}

const ProjectionUpdateBanner: React.FC<ProjectionUpdateBannerProps> = ({ gamePlayerIds }) => {
  const dispatch = useDispatch();
  const { playerCustomizations, projectionsUpdatedSinceCustomization, lastUpdatedTime } =
    useSelector((state: IRootState) => state.stokasticProps);

  // Check if any players in this game have customizations
  const hasCustomizations = gamePlayerIds.some((id) => playerCustomizations[id]);

  // Only show banner if projections updated AND user has customizations
  const showBanner = projectionsUpdatedSinceCustomization && hasCustomizations;

  if (!showBanner) {
    return null;
  }

  const handleKeepAdjustments = () => {
    dispatch(setProjectionsUpdatedSinceCustomization(false));
  };

  const handleResetToBaseline = () => {
    dispatch(resetGameCustomizations(gamePlayerIds));
    dispatch(setProjectionsUpdatedSinceCustomization(false));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(66, 165, 245, 0.1)',
        border: '1px solid rgba(66, 165, 245, 0.3)',
        borderRadius: 1,
        px: 2,
        py: 1.5,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.primary">
          Projections updated{lastUpdatedTime ? ` at ${lastUpdatedTime}` : ''}.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your adjustments are still applied.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={handleKeepAdjustments}
          sx={{
            textTransform: 'none',
            color: 'text.primary',
            fontSize: '13px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          Keep adjustments
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={handleResetToBaseline}
          sx={{
            textTransform: 'none',
            color: '#42A5F5',
            fontSize: '13px',
            '&:hover': {
              backgroundColor: 'rgba(66, 165, 245, 0.1)',
            },
          }}
        >
          Reset to new baseline
        </Button>
        <Box
          component="button"
          onClick={handleKeepAdjustments}
          sx={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <X size={16} weight="bold" />
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectionUpdateBanner;
